import { gql, GraphQLClient } from 'graphql-request';
import { Events, pubsub } from '@/javascript/service/pubsub';
import { Storage } from '@/javascript/service/storage';
import logger from '@service/logger';

class ShopifyServiceConfiguration {
  accessToken = '9749ee2a87028098fc8c6a0b3ae94326';
  storefront = '7rn2gx-vx.myshopify.com';
  apiVersion = '2024-10';
}

export class ShopifyService {
  /** @type {ShopifyServiceConfiguration} */
  config = new ShopifyServiceConfiguration();

  /** @type {Storage} */
  storage;

  /** @type {string} */
  cartId;

  /** @type {GraphQLClient} */
  client;

  _createClient() {
    this.client = new GraphQLClient(
      `https://${this.config.storefront}/api/${this.config.apiVersion}/graphql.json`,
      {
        headers: {
          'X-Shopify-Storefront-Access-Token': this.config.accessToken,
          'Content-Type': 'application/json',
        },
      }
    );
  }

  /**
   * @param {string} cartId - The ID of the cart to get.
   * @param {boolean} useExisting - Whether to use the existing cart ID if available.
   * @returns {Promise<import('@/javascript/model/cart').Cart>}
   * */
  async getCart(cartId, useExisting = true) {
    if (!cartId) {
      if (useExisting) {
        // Try to get the cart id from myself
        cartId = this.cartId;
      }
    }

    const query = gql`
      query cart($cartId: ID!) {
        cart(id: $cartId) {
          id
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
        }
      }
    `;
    const variables = {
      cartId: cartId,
    };
    try {
      const response = await this.client.request(query, variables);
      return response.cart;
    } catch (e) {
      console.error('Failed to get cart:', e);
      return null;
    }
  }

  _notifyCartId(cartId) {
    pubsub.pub(Events.cartIdResolved, cartId);
  }

  /** @returns {Promise<string>} */
  async ensureCart() {
    // Is there any cart information?
    const key = 'cartId';
    const cartObject = this.storage.object;
    const existingCartId = cartObject[key];
    let shouldCreateCart = true;
    if (!!existingCartId?.trim().length) {
      // Make sure the cart exists and is still valid
      const existingCart = await this.getCart(existingCartId);
      if (existingCart?.id) {
        this.cartId = existingCart.id;
        this._notifyCartId(this.cartId);
        shouldCreateCart = false;
        return this.cartId;
      }
    }

    if (shouldCreateCart) {
      const freshCartId = await this.createCart();
      cartObject[key] = freshCartId;
      this.storage.write(cartObject);
      // Tell the world that we have a cart id
      this.cartId = freshCartId;
      this._notifyCartId(this.cartId);
      return freshCartId;
    }

    return null;
  }

  /** @returns {Promise<boolean>} */
  async _observeLocation() {
    const storedZipCode = sessionStorage.getItem('customerZipCode');
    if (!!storedZipCode?.trim().length) {
      const zipCode = storedZipCode;
      const didApplyZipCode = await this.applyZipCode(zipCode);
      if (didApplyZipCode) {
        logger.debug(`ZIP Code [${zipCode}] has been applied to cart [${this.cartId}].`);
      } else {
        logger.warn(`Failed to apply ZIP Code [${zipCode}] to cart [${this.cartId}].`);
      }
      return didApplyZipCode;
    }
    logger.debug(`There is no zip code to apply. The cart will not be modified.`);
    return false;
  }

  /** @returns {boolean} */
  async _listen() {
    pubsub.sub(Events.zipCodeResolved, async zipCodeData => {
      if (!!zipCodeData.hasOwnProperty('zip')) {
        const zipCode = zipCodeData.zip;
        await this.applyZipCode(zipCode);
      }
    });

    pubsub.sub(Events.zipCodeCollected, async zipCode => {
      await this.applyZipCode(zipCode);
    });

    const cartId = await this.ensureCart();
    const location = await this._observeLocation();

    let maybe = false;
    maybe = maybe || !!cartId?.trim().length;

    return maybe;
  }

  constructor() {
    this.storage = Storage.sessionForKey('shopify');
    this._createClient();

    this._listen();
  }

  /**
   * Applies a zip code to the cart.
   * @param {string} zipCode - The zip code to apply.
   * @returns {Promise<boolean>} - A promise that resolves to true if the zip code was applied successfully, or false otherwise.
   */
  async applyZipCode(zipCode) {
    const query = gql`
      mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
        cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
          cart {
            id
            cost {
              totalAmount {
                amount
                currencyCode
              }
            }
            buyerIdentity {
              countryCode
              deliveryAddressPreferences {
                ... on MailingAddress {
                  zip
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      cartId: this.cartId,
      buyerIdentity: {
        deliveryAddressPreferences: [
          {
            deliveryAddress: {
              country: 'US',
              zip: zipCode,
            },
          },
        ],
      },
    };

    try {
      const response = await this.client.request(query, variables);
      return !!response;
    } catch (e) {
      // This may not have a cart id. Try to get one and then try again.

      if (!this.cartId) {
        this.cartId = await this.ensureCart();
        if (!!this.cartId?.trim().length) {
          return this.applyZipCode(zipCode);
        } else {
          console.error('Failed to apply zip code:', e);
          return false;
        }
      }
      console.error('Failed to apply zip code:', e);
      return false;
    }
  }

  /**
   * Creates a new cart in Shopify.
   * @param input
   * @returns {Promise<string>} - The ID of the created cart.
   * @throws {Error} - If there is an error creating the cart.
   */
  async createCart(input = {}) {
    const mutation = gql`
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const data = await this.client.request(mutation, { input });

      if (data.cartCreate.userErrors.length > 0) {
        const error = new Error(data.cartCreate.userErrors.map(e => e.message).join(', '));
        throw error;
      }

      const id = data.cartCreate.cart.id;
      return id;
    } catch (error) {
      console.error('Failed to create cart:', error);
      throw error;
    }
    return null;
  }


  /**
   * Returns the `window.meta` object provided by Shopify (if possible).
   *
   * @returns {{}|*}
   */
  get meta() {
    if ("meta" in window) {
      return window.meta;
    }
    return {};
  }

  /**
   * Returns the `window.meta.product` object provided by Shopify (if possible).
   * @returns {{}|*}
   */
  get productMeta() {
    const meta = this.meta;
    if ("product" in meta) {
      return meta.product;
    }
    return {};
  }

  /**
   * Returns the first element of the `window.meta.product.variants` array (if possible).
   * @returns {*|null}
   */
  get firstProductVariantOrNull() {
    const variants = this.productMeta.variants;
    if (!variants || variants.length === 0) {
      return null;
    }
    return variants[0];
  }

  get isProductView() {
    const firstVariant = this.firstProductVariantOrNull;
    const tests = [];
    tests.push(!!firstVariant);
    tests.push(!isNaN(Number(firstVariant.id)));
    const maybe = tests.every(it => it);
    return maybe;
  }
}

const shopifyService = new ShopifyService();
export default shopifyService;
